import { gray } from '@ant-design/colors';
import {
  HomeOutlined,
  DownOutlined,
  CopyOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { Breadcrumb, Tooltip, Dropdown, Button } from 'antd';
import { Link, useParams, useLocation } from 'react-router-dom';
import { FunctionComponent, useMemo } from 'react';
import { capitalCase } from 'change-case';
import { onClickCopy } from 'packages/lib/utils/on-click-copy';
import { useRecoilValue } from 'recoil';
import { personaAtom } from 'packages/at-shell/state/recoil/ui';
import { useTenantBasepath } from 'packages/lib/auth/is-path';
import { isInternalPath } from 'packages/lib/auth/is-path';
import {
  useTaskByTokenSWR,
  useTaskGroupByTokenSWR,
} from 'packages/lib/api/annotations/hooks/task';
import { TaskGroup } from 'packages/lib/api/annotations/contract/task-group';
import { useTenant } from 'packages/lib/auth/hooks/tenant';

function getDropdownItems(
  tool: string,
  path: string,
  isContractor: boolean,
  task_token: string,
  taskGroup: TaskGroup | undefined
) {
  const dropdownItems = [
    {
      key: `${tool}-breadcrumbs-dropdown-share-view`,
      label: (
        <Button
          type="text"
          style={{
            textDecoration: 'none',
            padding: 0,
            margin: 0,
            height: 'auto',
          }}
          onClick={(): void =>
            void onClickCopy(
              `${window.location.origin}${path}`,
              'Read-only viewer link'
            )
          }
        >
          Copy Viewer Link
        </Button>
      ),
      icon: <CopyOutlined />,
    },
  ];

  if (!isContractor) {
    dropdownItems.push({
      key: `${tool}-breadcrumbs-dropdown-task-link`,
      label: (
        <Link to={`/task/${task_token}`} target="_blank">
          <Button
            type="text"
            style={{
              textDecoration: 'none',
              padding: 0,
              margin: 0,
              height: 'auto',
            }}
          >
            Open Task
          </Button>
        </Link>
      ),
      icon: <LinkOutlined />,
    });

    if (taskGroup?.sampleGroupToken) {
      dropdownItems.push({
        key: `${tool}-breadcrumbs-dropdown-sample-group-link`,
        label: (
          <Link
            to={`/sample-group/${taskGroup.sampleGroupToken}`}
            target="_blank"
          >
            <Button
              type="text"
              style={{
                textDecoration: 'none',
                padding: 0,
                margin: 0,
                height: 'auto',
              }}
            >
              Open Sample Group
            </Button>
          </Link>
        ),
        icon: <LinkOutlined />,
      });
    }
  }

  return dropdownItems;
}

export interface ToolRouteParams {
  extraction_token?: string;
  task_token: string;
}
export const Breadcrumbs: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const path = useLocation().pathname;
  const persona = useRecoilValue(personaAtom);
  const pathParts = path.split('/');
  const basePath = useTenantBasepath();
  const isInternal = isInternalPath(basePath);
  const toolType = isInternalPath(basePath) ? pathParts[1] : pathParts[2];
  const tool = capitalCase(toolType);
  const { task_token } = useParams<ToolRouteParams>();

  const { data: task } = useTaskByTokenSWR(task_token, {
    enabled: isInternal,
    showErrorToast: true,
  });
  const { data: taskGroup } = useTaskGroupByTokenSWR(task?.taskGroupToken, {
    enabled: isInternal,
    showErrorToast: true,
  });

  const { data: tenant } = useTenant();
  const isContractor = tenant === 'contractor';

  const breadcrumbs = useMemo(() => {
    const crumbs = [
      {
        key: '/',
        title: (
          <Tooltip title="Go home">
            <Link to={basePath} style={{ color: gray[1] }}>
              <HomeOutlined style={{ marginRight: '0.25rem' }} />
            </Link>
          </Tooltip>
        ),
      },
      {
        key: `${tool}`,
        title: !task_token ? (
          <span data-testid="tool-page-name" style={{ color: 'white' }}>
            {tool}
          </span>
        ) : (
          <Dropdown
            placement="bottom"
            arrow
            menu={{
              items: getDropdownItems(
                tool,
                path,
                isContractor,
                task_token,
                taskGroup
              ),
            }}
          >
            <Button
              type="text"
              onClick={(e): void => e.preventDefault()}
              style={{
                color: 'white',
                textDecoration: 'none',
                padding: 0,
                margin: 0,
                height: 'auto',
              }}
            >
              {tool}
              <span style={{ color: gray[1] }}>
                <span style={{ margin: '0 0.5rem 0 0.75rem' }}>/</span>{' '}
                {capitalCase(persona)} Mode
              </span>
              <DownOutlined style={{ color: gray[1], marginLeft: '0.5rem' }} />
            </Button>
          </Dropdown>
        ),
      },
    ];

    return crumbs;
  }, [basePath, path, persona, task_token, tool, taskGroup, isContractor]);

  return (
    <div
      style={{
        marginLeft: '1.75rem',
        fontSize: '1rem',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Breadcrumb
        separator={
          <span
            style={{ color: gray[1], margin: '0 0.5rem', userSelect: 'none' }}
          >
            /
          </span>
        }
        items={breadcrumbs}
      ></Breadcrumb>
    </div>
  );
};
